import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";

import Styles from "./AvailableListing.module.css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CallMadeIcon from "@mui/icons-material/CallMade";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";

// Components
import PlaceBid from "../../../Modals/ConGen/PlaceBid/PlaceBid";
import AvailableListingSkeleton from "../../../../commons/AvailableCounterListingSkeleton";
import ViewInjectionModal from "../../../Modals/ConGen/ViewInjectionModal/ViewInjectionModal";
import ConGenListingModal from "../../../Modals/ConGen/ConGenListingModal";
import ConnectionModal from "../../../Modals/ConGen/Connection/ConnectionModal";
import ViewFinalizeTerms from "../../../Modals/ConGen/ViewFinalizeTerms/ViewFinalizeTerms";
import Charges from "../../../Modals/ConGen/Charges/Charges";
import ViewBG from "../../../Modals/ConGen/ViewBG/ViewBG";
import UploadConsent from "../../../Modals/ConGen/UploadConsent/UploadConsent";
import ViewConsent from "../../../Modals/ConGen/ViewConsent/ViewConsent";
import UploadLDC from "../../../Modals/ConGen/UploadLCD/UploadLDC";
import ScheduleModal from "../../../Modals/Schedule/ScheduleModal";

// Services and Redux
import {
  editListing,
  placeBidApi,
} from "../../../../Services/BuySell/Creation";
import {
  emptyCounterPartyList,
  fetchActiveConnection,
  fetchCounterPartList,
  fetchLedgerBids,
  fetchMainScreenActiveConnection,
  fetchSelfCapacity,
  fetchSelfCapacityById,
  setActiveListingId,
  triggerSelfListingRefresh,
} from "../../../../Redux/Slices/CapacitySlice";
import { fetchSchedule } from "../../../../Redux/Slices/scheduleSlice";

// Hooks and Utils
import { usePdfPreview } from "../../../../hooks/usePdfPreview";
import { formatDate, formatSlotTime } from "../../../../helpers/dateFormat";

// Assets
import emptyAvailableListing from "../../../../assets/images/emptyAvailableListing.svg";
import AcceptTermsCondition from "../../../Modals/ConGen/AcceptTermsCondition/AcceptTermsCondition";
import {
  closeListing,
  withdrawTerms,
} from "../../../../Services/BuySell/Listing";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../commons/Toast/ErrorMessage";
import UploadDocModal from "../../../Modals/ConGen/UploadFiles/UploadFiles";
import { checkApprovedAndPending } from "../../../../Redux/Slices/buySellhasApprovedAndPending";
import ViewProfile from "../../../Modals/ConGen/ViewProfile/ViewProfile";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import { openBilateralChat } from "../../../Modals/Banking/CreateEdit/helpers";
import ViewDoc from "../../../Modals/ConGen/ViewDoc/ViewDoc";
import TermsCondition from "./TermsCondition/TermsCondition";
import { get_STOA } from "../../../../Services/ApiCalling/document";
import DirectDocumentation from "../../../Modals/Common/DirectDocumentation/DirectDocumentation";

// Constants
export const STATUSES = {
  ACTIVE: "ACTIVE",
  WITHDRAWN: "WITHDRAWN",
  PLACED: "PLACED",
  RECEIVED: "RECEIVED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  FINAL: "ONGOING",
  CLOSED: "CLOSED",
  TERMS_INITIATED: "TERMS_INITIATED",
  TERMS_INITIATE_AND_ACCEPT: "TERMS_INITIATE_AND_ACCEPT",
  TERMS_ACCEPTED: "TERMS_ACCEPTED",
  PENDING: "PENDING",
  AGREEMENT_ACCEPTED: "AGREEMENT_ACCEPTED",
  SCHEDULED: "SCHEDULED",
  PARTIAL_PENDING: "PARTIAL_PENDING",
};
export const STATUS_LABELS = {
  ACTIVE: "Published",
  WITHDRAWN: "Withdrawn",
  PLACED: "Placed",
  RECEIVED: "Received",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  ONGOING: "Ongoing",
  CLOSED: "Closed",
  TERMS_INITIATED: "Documentation Incomplete",
  TERMS_INITIATE_AND_ACCEPT: "Documentation Incomplete",
  TERMS_ACCEPTED: "Documentation Incomplete",
  PENDING: "Pending",
  AGREEMENT_ACCEPTED: "Scheduling Not Done",
  SCHEDULED: "Scheduling Completed",
  PARTIAL_PENDING: "Partial Pending",
};

export const STATUS_COLORS = {
  ACTIVE: { bg: "#e8f4ea", text: "#2e7d32" },
  WITHDRAWN: { bg: "#fdecea", text: "#c62828" },
  PLACED: { bg: "#e8f4ea", text: "#2e7d32" },
  RECEIVED: { bg: "#e8f4ea", text: "#2e7d32" },
  APPROVED: { bg: "#e8f4ea", text: "#2e7d32" },
  REJECTED: { bg: "#fdecea", text: "#c62828" },
  ONGOING: { bg: "#fff3e0", text: "#e65100" },
  CLOSED: { bg: "#e8f4ea", text: "#2e7d32" },
  TERMS_INITIATED: { bg: "#fdecea", text: "#c62828" },
  TERMS_INITIATE_AND_ACCEPT: { bg: "#fdecea", text: "#c62828" },
  TERMS_ACCEPTED: { bg: "#fdecea", text: "#c62828" },
  PENDING: { bg: "#fff3e0", text: "#e65100" },
  AGREEMENT_ACCEPTED: { bg: "#fdecea", text: "#c62828" },
  SCHEDULED: { bg: "#e8f4ea", text: "#2e7d32" },
  PARTIAL_PENDING: { bg: "#fff3e0", text: "#e65100" },
};

// Add a pseudo-status to represent user-initiated pending items that should be editable
const EDIT_PENDING_STATUS = "EDIT_PENDING";

const USER_TYPES = {
  CONSUMER: "consumer",
  GENERATOR: "generator",
};

// Helper functions
const getEnergyRate = (element) => {
  return Number(
    element?.slots?.[0]?.sellingCost ?? element?.slots?.[0]?.landingCost ?? 0,
  );
};

const getQuantity = (element) => {
  return Number(
    element?.slots?.[0]?.sellEnergy ?? element?.slots?.[0]?.purchaseEnergy ?? 0,
  );
};

const hasApprovedStatus = (list) => {
  return (
    Array.isArray(list) &&
    list?.some((item) => item?.status === STATUSES.APPROVED)
  );
};

// Custom hooks
export const useModals = () => {
  const [modals, setModals] = useState({
    terms: false,
    charges: false,
    bg: false,
    docs: false,
    consent: false,
    schedule: false,
    uploadFiles: false,
    approveDocs: false,
    uploadConsent: false,
    uploadLDC: false,
    withdrawTerms: false,
    editDocs: false,
    editTerms: false,
  });

  const toggleModal = useCallback((modalName, isOpen) => {
    setModals((prev) => ({ ...prev, [modalName]: isOpen }));
  }, []);

  return { modals, toggleModal };
};

export const useConnectionData = () => {
  const location = useLocation();

  const user = useSelector(
    (state) => state?.persistedReducer?.userType,
  )?.toLowerCase();
  const activeMainId =
    useSelector((state) => state?.CapacitySlice?.activeMain?.id) ||
    location?.search?.split("=")[1];
  const list = useSelector((state) => state?.CapacitySlice?.list);
  const bilateralConnections = useSelector(
    (state) => state?.CapacitySlice?.activeConnection,
  );
  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection,
  );

  const finFinal = useMemo(
    () => list?.find((ele) => ele?.id == activeMainId),
    [list, activeMainId],
  );

  return {
    user,
    finFinal,
    bilateralConnections,
    mainScreenMatchData,
  };
};

// Empty State Component
const EmptyState = React.memo(() => (
  <div className={Styles.emptyState}>
    <img
      style={{ marginBottom: "10px" }}
      src={emptyAvailableListing}
      alt="No Available Listings"
    />
    <h2>No Matching</h2>
    <h2>Counterparties Available</h2>
    <p>
      There are no counterparties that match your requirements. We'll inform you
      as soon as a match is found
    </p>
  </div>
));

EmptyState.displayName = "EmptyState";

// Finalize Button Component
const FinalizeButton = React.memo(({ onClick }) => (
  <div className={Styles.finalizeContainer}>
    <button type="button" onClick={onClick}>
      Finalize
    </button>
  </div>
));

FinalizeButton.displayName = "FinalizeButton";

const getFarthestDateTimeWith24HourBuffer = (connection) => {
  const bilateralConnections = connection?.bilateralConnections;

  if (
    !Array.isArray(bilateralConnections) ||
    bilateralConnections.length === 0
  ) {
    return null;
  }

  // Find the connection with the farthest dateEndAt
  const farthestConnection = bilateralConnections.reduce(
    (farthest, current) => {
      const farthestDate = new Date(farthest.dateEndAt);
      const currentDate = new Date(current.dateEndAt);
      return currentDate > farthestDate ? current : farthest;
    },
  );

  const farthestDate = farthestConnection.dateEndAt;
  const farthestTime = farthestConnection.slotEndAt;

  // Combine date and time, then add 24 hours
  const dateTimeString = `${farthestDate}T${farthestTime}`;
  const dateTime = new Date(dateTimeString);

  // Add 24 hours (in milliseconds)
  const finalDateTime = new Date(dateTime.getTime() + 24 * 60 * 60 * 1000);

  return {
    farthestDate,
    farthestTime,
    finalDateTime: finalDateTime.toISOString(),
    finalDate: finalDateTime.toISOString().split("T")[0],
    finalTime: finalDateTime.toTimeString().slice(0, 5), // HH:MM format
  };
};

// Left Side Buttons Component
export const LeftSideBtns = React.memo(
  ({
    clickedIndex,
    isReverseAuction,
    searchCapId,
    reverseAuctionBids,
    viewRightButtons,
  }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { user, finFinal, mainScreenMatchData } = useConnectionData();
    const { modals, toggleModal } = useModals();
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const isTenderActiveScreen = location?.pathname?.includes("/tender_active");

    const connection = useMemo(() => {
      return mainScreenMatchData?.[clickedIndex];
    }, [mainScreenMatchData, clickedIndex]);

    const handleViewSchedule = useCallback(async () => {
      if (!connection?.id) return;

      dispatch(fetchSchedule(connection.id));
      setTimeout(() => {
        toggleModal("schedule", true);
      }, 1000);
    }, [connection?.id, dispatch, toggleModal]);

    const finalTimeInfo = useMemo(() => {
      return getFarthestDateTimeWith24HourBuffer(connection);
    }, [connection]);

    const isClosingAllowed = useMemo(() => {
      if (!finalTimeInfo?.finalDateTime) return false;
      const now = new Date();
      const finalDate = new Date(finalTimeInfo.finalDateTime);
      return now >= finalDate;
    }, [finalTimeInfo]);

    const buttonConfig = useMemo(() => {
      const shouldShowViewTerms = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conTermsAcceptanceStatus &&
            !connection?.genTermsAcceptanceStatus) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genTermsAcceptanceStatus &&
            !connection?.conTermsAcceptanceStatus)
        );
      };
      const shouldShowViewFinalizedTerms = () => {
        return (
          connection?.conTermsAcceptanceStatus &&
          connection?.genTermsAcceptanceStatus
        );
      };

      const shouldShowViewCharges = () => {
        return finFinal?.status !== STATUSES.TERMS_INITIATED;
      };

      const shouldShowViewDocs = () => {
        return (
          connection?.genAgreementAcceptanceStatus &&
          connection?.conAgreementAcceptanceStatus
        );
      };

      const shouldShowEditDocs = () => {
        if (!viewRightButtons) {
          return false;
        }
        return (
          (user === USER_TYPES.GENERATOR &&
            connection?.genAgreementAcceptanceStatus &&
            !connection?.conAgreementAcceptanceStatus) ||
          (user === USER_TYPES.CONSUMER &&
            connection?.conAgreementAcceptanceStatus &&
            !connection?.genAgreementAcceptanceStatus)
        );
      };

      const shouldShowViewConsent = () => {
        return (
          (user === USER_TYPES.CONSUMER && connection?.conConsentDocId) ||
          (user === USER_TYPES.GENERATOR && connection?.genConsentDocId)
        );
      };

      const shouldShowViewSchedule = () => {
        return (
          (connection?.status === STATUSES.SCHEDULED && isClosingAllowed) ||
          // Archived/closed connections keep status === "CLOSED" but expose a
          // separate schedulingStatus. Show "View Schedule" whenever a schedule
          // has actually been generated for such closed connections.
          (connection?.status === STATUSES.CLOSED &&
            connection?.schedulingStatus === STATUSES.SCHEDULED)
        );
      };

      return [
        // {
        //   show: shouldShowViewTerms() && viewRightButtons,
        //   onClick: () => toggleModal("terms", true),
        //   text: "View Draft T&C",
        // },
        // {
        //   show:
        //     viewRightButtons &&
        //     (isReverseAuction ? false : shouldShowViewFinalizedTerms()),
        //   onClick: () => toggleModal("terms", true),
        //   text: "View Finalized T&C",
        // },
        // {
        //   show: viewRightButtons && shouldShowViewCharges(),
        //   onClick: () => toggleModal("charges", true),
        //   text: "View Charges",
        // },
        // {
        //   show:
        //     viewRightButtons && isTenderActiveScreen && !shouldShowViewDocs(),
        //   onClick: () => toggleModal("bg", true),
        //   text: "BG",
        // },
        // {
        //   show: viewRightButtons && shouldShowEditDocs(),
        //   onClick: () => toggleModal("editDocs", true),
        //   text: "Edit Docs",
        // },
        // {
        //   show: viewRightButtons && shouldShowViewDocs(),
        //   onClick: () => toggleModal("docs", true),
        //   text: "View Docs",
        // },
        // {
        //   show: viewRightButtons && shouldShowViewConsent(),
        //   onClick: () => toggleModal("consent", true),
        //   text: "View Consent / LDC",
        // },
        // {
        //   show: shouldShowViewSchedule(),
        //   onClick: handleViewSchedule,
        //   text: "View Schedule",
        // },
      ];
    }, [
      isReverseAuction,
      handleViewSchedule,
      user,
      connection?.conTermsAcceptanceStatus,
      connection?.genTermsAcceptanceStatus,
      connection?.genAgreementAcceptanceStatus,
      connection?.conAgreementAcceptanceStatus,
      connection?.conConsentDocId,
      connection?.genConsentDocId,
      connection?.status,
      connection?.schedulingStatus,
      finFinal?.status,
      viewRightButtons,
      isClosingAllowed,
      toggleModal,
      reverseAuctionBids,
      isTenderActiveScreen,
    ]);

    if (!connection) {
      return null;
    }

    function getAllContractIds(data) {
      const ids = [];

      // Top-level contractId (if present)
      if (data?.capacityConsumer?.contractId !== undefined) {
        ids.push(data.capacityConsumer.contractId);
      }
      if (data?.capacityGenerator?.contractId !== undefined) {
        ids.push(data.capacityGenerator.contractId);
      }

      // From matches.generators
      if (Array.isArray(data?.matches)) {
        data.matches.forEach((match) => {
          (match.generators || []).forEach((gen) => {
            if (gen.contractId !== undefined) {
              ids.push(gen.contractId);
            }
          });
          (match.consumers || []).forEach((con) => {
            if (con.contractId !== undefined) {
              ids.push(con.contractId);
            }
          });
        });
      }

      // Remove duplicates
      return Array.from(new Set(ids));
    }

    async function handleWithdraw() {
      const finalizedIds = getAllContractIds(connection);
      if (finalizedIds?.length === 0) {
        return ErrorMessage("Please Select Requisition(s)");
      }
      let payload = {
        contractIds: finalizedIds,
        capacityId: finFinal.id,
      };
      try {
        setWithdrawLoading(true);
        const res = await withdrawTerms(payload);
        if (res?.data?.code === 200) {
          // dispatch(fetchActiveConnection({ curr: 0, capId: listingId }));
          dispatch(fetchSelfCapacity({ user }));
          toggleModal("withdrawTerms", false);
        }
      } catch (error) {
      } finally {
        setWithdrawLoading(false);
      }
    }

    const shouldShowViewFinalizedTerms = () => {
      return (
        connection?.conTermsAcceptanceStatus &&
        connection?.genTermsAcceptanceStatus
      );
    };

    return (
      <>
        {/* Modals */}
        {modals.terms && (
          <ViewFinalizeTerms
            useSecondaryAccent
            open={modals.terms}
            handleClose={() => toggleModal("terms", false)}
            data={connection}
            drafted={shouldShowViewFinalizedTerms() ? false : true}
          />
        )}
        {modals.withdrawTerms && (
          <AcceptTermsCondition
            open={modals.withdrawTerms}
            handleClose={() => toggleModal("withdrawTerms", false)}
            data={connection}
            // handleTermsSubmit={handleTermsSubmit}
            wholeData={connection}
            handleWithdraw={handleWithdraw}
            withdrawLoading={withdrawLoading}
          />
        )}

        {modals.charges && (
          <Charges
            useSecondaryAccent
            open={modals.charges}
            onClose={() => toggleModal("charges", false)}
            contractId={connection?.id}
          />
        )}

        {modals.bg && (
          <ViewBG
            open={modals.bg}
            onClose={() => toggleModal("bg", false)}
            connectionId={connection?.id}
          />
        )}

        {modals.consent && (
          <ViewConsent
            useSecondaryAccent
            connectionId={connection.id}
            open={modals.consent}
            onClose={() => toggleModal("consent", false)}
          />
        )}

        {modals.docs && (
          <ViewDoc
            useSecondaryAccent
            connectionId={connection.id}
            open={modals.docs}
            handleClose={() => toggleModal("docs", false)}
          />
        )}
        {modals.editDocs && (
          <>
            <UploadDocModal
              useSecondaryAccent
              connectionId={connection.id}
              open={modals.editDocs}
              searchCapId={searchCapId}
              isReverseAuction={isReverseAuction}
              handleClose={() => {
                toggleModal("editDocs", false);
                dispatch(
                  fetchMainScreenActiveConnection({
                    capId: isReverseAuction ? searchCapId : finFinal.id,
                  }),
                );
              }}
            />
          </>
        )}

        {/* {modals.editDocs && (
          <DirectDocumentation
            mode="capacity"
            connectionId={connection.id}
            open={modals.editDocs}
            capacityId={isReverseAuction ? searchCapId : finFinal?.id}
            handleClose={() => {
              toggleModal("editDocs", false);
              dispatch(
                fetchMainScreenActiveConnection({
                  capId: isReverseAuction ? searchCapId : finFinal.id,
                }),
              );
            }}
            onAllDocsUploaded={() => {
              dispatch(
                fetchActiveConnection({
                  curr: 0,
                  capId: isReverseAuction ? searchCapId : finFinal.id,
                }),
              );
            }}
          />
        )} */}
        {console.debug("viewRightButtons", viewRightButtons)}
        {modals.schedule && (
          <ScheduleModal
            useSecondaryAccent
            connectionId={connection.id}
            open={modals.schedule}
            handleClose={() => toggleModal("schedule", false)}
            connection={connection}
            viewOnly={connection?.status === STATUSES.CLOSED}
            viewRightButtons={viewRightButtons}
          />
        )}

        {/* Buttons */}
        <div className={Styles.leftSideBtnContainer}>
          {buttonConfig
            .filter((button) => button.show)
            .map((button, index) => (
              <button
                key={`left-btn-${index}`}
                onClick={button.onClick}
                className={Styles.leftSideBtn}
              >
                {button.text}
                <CallMadeIcon
                  style={{
                    fontSize: "8px",
                    position: "absolute",
                    top: "-0px",
                  }}
                />
              </button>
            ))}
        </div>
      </>
    );
  },
);

LeftSideBtns.displayName = "LeftSideBtns";

// Right Side Buttons Component
export const RightSideBtn = React.memo(
  ({
    clickedIndex,
    finFinal,
    searchCapId,
    isReverseAuction,
    originalTenderCloseDateTime: originalTenderCloseDateTimeProp,
    viewRightButtons,
  }) => {
    const navigate = useNavigate();
    const activeListingId = useSelector(
      (state) => state?.CapacitySlice?.activeListingId,
    );
    const tenderSpecefic = useSelector(
      (state) => state?.auctionSlice?.tenderSpecefic,
    );
    const closeDateStr =
      originalTenderCloseDateTimeProp ||
      (isReverseAuction ? tenderSpecefic?.originalTenderCloseDateTime : null);
    const isCloseDatePassed =
      !!closeDateStr && new Date(closeDateStr) < new Date();
    const { user, mainScreenMatchData } = useConnectionData();
    const { modals, toggleModal } = useModals();
    const dispatch = useDispatch();
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const listingId = useSelector(
      (state) => state?.CapacitySlice?.activeListingId,
    );
    const [closeLoading, setCloseLoading] = useState(false);
    const [closeConfirmModal, setCloseConfirmModal] = useState(false);

    const connection = useMemo(() => {
      return mainScreenMatchData?.[clickedIndex];
    }, [mainScreenMatchData, clickedIndex]);

    const finalTimeInfo = useMemo(() => {
      return getFarthestDateTimeWith24HourBuffer(connection);
    }, [connection]);

    const isClosingAllowed = useMemo(() => {
      if (!finalTimeInfo?.finalDateTime) return false;
      const now = new Date();
      const finalDate = new Date(finalTimeInfo.finalDateTime);
      return now >= finalDate;
    }, [finalTimeInfo]);

    const conditionalHelpers = useMemo(() => {
      const shouldWithdraw = () => {
        return (
          (finFinal?.status === STATUSES.TERMS_INITIATED &&
            connection?.status === STATUSES.PENDING &&
            connection?.initiatorType === "consumer" &&
            user === USER_TYPES.CONSUMER) ||
          (finFinal?.status === STATUSES.TERMS_INITIATED &&
            connection?.status === STATUSES.PENDING &&
            connection?.initiatorType === "generator" &&
            user === USER_TYPES.GENERATOR)
        );
      };
      const shouldShowUploadDocs = () => {
        return (
          connection?.status === STATUSES.TERMS_ACCEPTED &&
          !connection?.conAgreementAcceptanceStatus &&
          !connection?.genAgreementAcceptanceStatus
        );
      };

      const shouldShowWaitingDocApproval = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conAgreementAcceptanceStatus &&
            !connection?.genAgreementAcceptanceStatus) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genAgreementAcceptanceStatus &&
            !connection?.conAgreementAcceptanceStatus)
        );
      };

      const shouldShowWaitingTermsApproval = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.conTermsAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.genTermsAcceptanceStatus)) &&
          connection?.status === STATUSES.PENDING
        );
      };

      const shouldShowTermsApproved = () => {
        return (
          connection?.status === STATUSES.TERMS_INITIATED &&
          (user === USER_TYPES.CONSUMER || user === USER_TYPES.GENERATOR) &&
          connection?.genTermsAcceptanceStatus &&
          connection?.conTermsAcceptanceStatus &&
          mainScreenMatchData.length > 1
        );
      };

      const shouldShowWaitingOtherParties = () => {
        return (
          connection?.status === STATUSES.TERMS_INITIATED &&
          (user === USER_TYPES.CONSUMER || user === USER_TYPES.GENERATOR) &&
          connection?.genTermsAcceptanceStatus &&
          connection?.conTermsAcceptanceStatus &&
          mainScreenMatchData.length === 1
        );
      };

      const shouldShowWaitingConsentUpload = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conConsentDocId &&
            !connection?.genConsentDocId) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genConsentDocId &&
            !connection?.conConsentDocId)
        );
      };

      const shouldShowApproveDocs = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.genAgreementAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.conAgreementAcceptanceStatus)) &&
          connection?.status !== STATUSES.AGREEMENT_ACCEPTED &&
          connection?.status !== STATUSES.SCHEDULED
        );
      };

      const shouldShowUploadConsent = () => {
        return (
          connection?.status === STATUSES.AGREEMENT_ACCEPTED &&
          !(
            (user === USER_TYPES.CONSUMER && connection?.conConsentDocId) ||
            (user === USER_TYPES.GENERATOR && connection?.genConsentDocId)
          )
        );
      };

      const shouldShowApproveTerms = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.genTermsAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.conTermsAcceptanceStatus)) &&
          connection?.status === STATUSES.PENDING
        );
      };

      const shouldShowGenerateSchedule = () => {
        return (
          connection?.status === STATUSES.AGREEMENT_ACCEPTED &&
          connection?.conConsentDocId &&
          connection?.genConsentDocId &&
          connection?.status !== STATUSES.SCHEDULED
        );
      };
      const shouldShowViewSchedule = () => {
        return connection?.status === STATUSES.SCHEDULED && !isClosingAllowed;
      };
      const shouldShowClose = () => {
        return isClosingAllowed && connection?.status === STATUSES.SCHEDULED;
      };

      return {
        shouldShowUploadDocs,
        shouldShowWaitingDocApproval,
        shouldShowWaitingTermsApproval,
        shouldShowTermsApproved,
        shouldWithdraw,
        shouldShowWaitingOtherParties,
        shouldShowWaitingConsentUpload,
        shouldShowApproveDocs,
        shouldShowUploadConsent,
        shouldShowApproveTerms,
        shouldShowGenerateSchedule,
        shouldShowClose,
        shouldShowViewSchedule,
      };
    }, [
      connection?.conAgreementAcceptanceStatus,
      connection?.conConsentDocId,
      connection?.conTermsAcceptanceStatus,
      connection?.genAgreementAcceptanceStatus,
      connection?.genConsentDocId,
      connection?.genTermsAcceptanceStatus,
      connection?.initiatorType,
      connection?.status,
      finFinal?.status,
      isClosingAllowed,
      mainScreenMatchData.length,
      user,
    ]);

    const statusMessages = useMemo(
      () => [
        // {
        //   show: conditionalHelpers.shouldShowWaitingDocApproval(),
        //   text: "Waiting Document Approval",
        // },
        // {
        //   show: conditionalHelpers.shouldShowWaitingTermsApproval(),
        //   text: "Waiting Terms Approval",
        // },
        // {
        //   show: conditionalHelpers.shouldShowApproveTerms(),
        //   text: "T&C Request Received",
        // },
        // {
        //   show: conditionalHelpers.shouldShowTermsApproved(),
        //   text: "Terms Approved",
        // },
        // {
        //   show: conditionalHelpers.shouldShowWaitingOtherParties(),
        //   text: "Waiting for other parties",
        // },
        // {
        //   show: conditionalHelpers.shouldShowWaitingConsentUpload(),
        //   text: "Waiting Consent Upload",
        // },
      ],
      [conditionalHelpers],
    );

    const handleWithdraw = useCallback(
      async (id) => {
        const finalizedIds = [id];
        if (finalizedIds?.length === 0) {
          return ErrorMessage("Please Select Requisition(s)");
        }
        let payload = {
          contractIds: finalizedIds,
          capacityId: finFinal.id,
        };
        try {
          setWithdrawLoading(true);
          const res = await withdrawTerms(payload, true);
          if (res?.data?.code === 200) {
            dispatch(
              fetchActiveConnection({
                curr: 0,
                capId: listingId,
                isTermsAccepted: null,
              }),
            );

            const aa = {
              id: activeListingId,
              energySourceType: "",
              energyFuelType: "",
              tag: "",
            };
            dispatch(fetchCounterPartList({ user, payload: aa, curr: 0 }));
            dispatch(fetchSelfCapacity({ user }));
            dispatch(fetchLedgerBids(listingId));
            dispatch(fetchMainScreenActiveConnection({ capId: finFinal.id }));
            dispatch(listingId);
            toggleModal("withdrawTerms", false);
          }
        } catch (error) {
        } finally {
          setWithdrawLoading(false);
        }
      },
      [activeListingId, dispatch, finFinal?.id, listingId, toggleModal, user],
    );

    const handleViewSchedule = useCallback(async () => {
      if (!connection?.id) return;

      dispatch(fetchSchedule(connection.id));
      setTimeout(() => {
        toggleModal("schedule", true);
      }, 1000);
    }, [connection?.id, dispatch, toggleModal]);

    const DownloadSTOA = useCallback(async () => {
      try {
        for (let i = 1; i <= 2; i++) {
          const res = await get_STOA({ type: i === 1, id: connection?.id });
          const blob = new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `STOA_Template_${i}.docx`;
          link.style.display = "none";

          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
        }
        SuccessMessage("Downloaded successfully!");
      } catch (error) {
        ErrorMessage("Failed to download STOA document(s).");
      }
    }, [connection]);

    const handleClose = useCallback(async () => {
      try {
        setCloseLoading(true);
        const res = await closeListing({ contractId: connection?.id });
        if (res?.data?.code === 200) {
          SuccessMessage(
            isReverseAuction
              ? "Tender closed successfully"
              : "Listing closed successfully",
          );
          if (!window.location.pathname.includes("/dashboard")) {
            navigate("/dashboard");
          } else {
            dispatch(setActiveListingId(null));
            await dispatch(fetchSelfCapacity({ user })).unwrap();
            dispatch(emptyCounterPartyList());
            dispatch(triggerSelfListingRefresh());
            setCloseConfirmModal(false);
          }
        }
      } catch (error) {
        ErrorMessage("Failed to close listing");
      } finally {
        setCloseLoading(false);
      }
    }, [connection?.id, dispatch, navigate, user, isReverseAuction]);

    const buttons = useMemo(
      () => [
        // {
        //   show: viewRightButtons && conditionalHelpers.shouldWithdraw(),
        //   onClick: () => {
        //     toggleModal("withdrawTerms", true);
        //   },
        //   text: "Withdraw",
        //   disabled: !!isCloseDatePassed,
        // },
        // {
        //   show: viewRightButtons && conditionalHelpers.shouldShowUploadDocs(),
        //   onClick: () => toggleModal("uploadFiles", true),
        //   text: "Upload Docs",
        //   disabled: !!isCloseDatePassed,
        // },
        // {
        //   show: viewRightButtons && conditionalHelpers.shouldShowApproveDocs(),
        //   onClick: () => toggleModal("approveDocs", true),
        //   text: "Approve Docs",
        //   disabled: !!isCloseDatePassed,
        // },
        // {
        //   show:
        //     viewRightButtons && conditionalHelpers.shouldShowUploadConsent(),
        //   onClick: () => DownloadSTOA(),
        //   text: "Generate STOA",
        //   disabled: !!isCloseDatePassed,
        // },
        // {
        //   show:
        //     viewRightButtons && conditionalHelpers.shouldShowUploadConsent(),
        //   onClick: () => toggleModal("uploadConsent", true),
        //   text: "Upload Consent",
        //   disabled: !!isCloseDatePassed,
        // },
        // {
        //   show:
        //     viewRightButtons && conditionalHelpers.shouldShowGenerateSchedule(),
        //   onClick: () => toggleModal("uploadLDC", true),
        //   text: "Generate Schedule",
        //   disabled: !!isCloseDatePassed,
        // },
        // {
        //   show: viewRightButtons && conditionalHelpers.shouldShowViewSchedule(),
        //   onClick: () => handleViewSchedule(),
        //   text: "View Schedule",
        // },
        // {
        //   show: viewRightButtons && conditionalHelpers.shouldShowClose(),
        //   onClick: () => setCloseConfirmModal(true),
        //   text: "Close",
        //   loading: closeLoading,
        // },
      ],
      [
        DownloadSTOA,
        closeLoading,
        conditionalHelpers,
        handleViewSchedule,
        isCloseDatePassed,
        toggleModal,
      ],
    );
    if (!connection) {
      return null;
    }

    return (
      <>
        {/* Modals */}
        {modals.uploadFiles && (
          <UploadDocModal
            useSecondaryAccent
            data={connection}
            connectionId={connection.id}
            open={modals.uploadFiles}
            capacityId={isReverseAuction && searchCapId}
            handleClose={() => {
              toggleModal("uploadFiles", false);
              dispatch(
                fetchMainScreenActiveConnection({
                  capId: isReverseAuction ? searchCapId : finFinal.id,
                }),
              );
            }}
          />
        )}

        {/* {modals.uploadFiles && (
          <DirectDocumentation
            mode="capacity"
            data={connection}
            connectionId={connection.id}
            open={modals.uploadFiles}
            capacityId={isReverseAuction ? searchCapId : finFinal?.id}
            handleClose={() => {
              toggleModal("uploadFiles", false);
              dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
            }}
            onAllDocsUploaded={() => {
              dispatch(fetchActiveConnection({ curr: 0, capId: listingId }));
              dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
            }}
          />
        )} */}

        {closeConfirmModal && (
          <ConfirmationModal
            open={closeConfirmModal}
            handleClose={() => setCloseConfirmModal(false)}
            handleSubmit={handleClose}
            header={"Close Confirmation"}
            data={
              "Closing will move it to the archive. Do you want to continue?"
            }
            loading={closeLoading}
          />
        )}

        {modals.schedule && (
          <ScheduleModal
            useSecondaryAccent
            connectionId={connection.id}
            open={modals.schedule}
            handleClose={() => toggleModal("schedule", false)}
            connection={connection}
            viewRightButtons={viewRightButtons}
          />
        )}

        {modals.editTerms && (
          <TermsCondition
            open={modals.editTerms}
            handleClose={() => toggleModal("editTerms", false)}
            listingId={finFinal?.id}
            isEditTerms={true}
            connectionData={connection}
            useSecondaryAccent
          />
        )}

        {modals.withdrawTerms && (
          <ConfirmationModal
            open={modals.withdrawTerms}
            handleClose={() => toggleModal("withdrawTerms", false)}
            handleSubmit={() => handleWithdraw(connection.id)}
            header={"Withdraw Terms Confirmation"}
            data={"Are you sure you want to withdraw terms?"}
            loading={withdrawLoading}
          />
        )}

        {modals.approveDocs && (
          <UploadDocModal
            useSecondaryAccent
            agreementStatus={
              (user === "consumer" &&
                connection?.genAgreementAcceptanceStatus) ||
              (user === "generator" && connection?.conAgreementAcceptanceStatus)
            }
            data={connection}
            connectionId={connection.id}
            open={modals.approveDocs}
            handleClose={() => {
              toggleModal("approveDocs", false);
              dispatch(
                fetchMainScreenActiveConnection({
                  capId: isReverseAuction ? searchCapId : finFinal.id,
                }),
              );
            }}
            capacityId={isReverseAuction && searchCapId}
          />
        )}

        {/* {modals.approveDocs && (
          <DirectDocumentation
            mode="capacity"
            agreementStatus={
              (user === "consumer" &&
                connection?.genAgreementAcceptanceStatus) ||
              (user === "generator" && connection?.conAgreementAcceptanceStatus)
            }
            data={connection}
            connectionId={connection.id}
            open={modals.approveDocs}
            handleClose={() => {
              toggleModal("approveDocs", false);
              dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
            }}
            capacityId={isReverseAuction ? searchCapId : finFinal?.id}
            onAllDocsUploaded={() => {
              dispatch(fetchActiveConnection({ curr: 0, capId: listingId }));
              dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
            }}
          />
        )} */}

        {modals.uploadLDC && (
          <UploadLDC
            useSecondaryAccent
            connectionId={connection.id}
            clickedIndex={clickedIndex}
            open={modals.uploadLDC}
            onClose={() => toggleModal("uploadLDC", false)}
            capacityId={isReverseAuction && searchCapId}
            connection={connection}
          />
        )}

        {modals.uploadConsent && (
          <UploadConsent
            useSecondaryAccent
            connectionId={connection.id}
            open={modals.uploadConsent}
            onClose={() => toggleModal("uploadConsent", false)}
            capacityId={isReverseAuction && searchCapId}
          />
        )}

        {/* Status Messages */}
        {statusMessages
          .filter((message) => message.show)
          .map((message, index) => (
            <span
              key={`status-${index}`}
              className={Styles.waitingApprovalText}
            >
              {message.text}
            </span>
          ))}

        {/* Action Buttons */}
        {buttons
          .filter((button) => button.show)
          .map((button, index) => (
            <button
              style={{
                wordBreak: "keep-all",
                textWrap: "nowrap",
                ...(button.disabled
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : {}),
              }}
              disabled={button.loading || button.disabled}
              key={`right-btn-${index}`}
              onClick={button.onClick}
              className={
                button.text === "Withdraw"
                  ? Styles.withdrawBtn
                  : Styles.rightSideBtn
              }
            >
              {button.loading ? (
                <span>
                  {button.text === "Withdraw" ? "Withdrawing..." : "Closing..."}
                </span>
              ) : (
                button.text
              )}
            </button>
          ))}
      </>
    );
  },
);

RightSideBtn.displayName = "RightSideBtn";

// Status Button Component
const StatusButton = React.memo(
  ({
    element,
    handleConnection,
    setActiveCounterId,
    setOpenEditListingModal,
    setClickedBtn,
    setConnectionDetails,
  }) => {
    const statusActions = {
      [STATUSES.ACTIVE]: () => (
        <div
          onClick={() => handleConnection(element.id)}
          className={`display_flex_row ${Styles.buttonElementCon}`}
        >
          <PersonAddAltIcon style={{ fontSize: "16px" }} />
          <div>Connect</div>
        </div>
      ),
      [STATUSES.WITHDRAWN]: () => (
        <div
          onClick={() => handleConnection(element.id)}
          className={`display_flex_row ${Styles.buttonElementCon}`}
        >
          <PersonAddAltIcon style={{ fontSize: "16px" }} />
          <div>Connect</div>
        </div>
      ),
      [STATUSES.PLACED]: () => (
        <div
          onClick={() => {
            setActiveCounterId(element.id);
            setOpenEditListingModal(true);
          }}
          className={`display_flex_row ${Styles.editButtonElementCon}`}
        >
          <div>Edit Connection Request</div>
        </div>
      ),
      [STATUSES.RECEIVED]: () => (
        <div
          onClick={() => {
            setClickedBtn(element.id);
            setConnectionDetails(true);
          }}
          className={`display_flex_row ${Styles.buttonElementCon}`}
        >
          <div>Received</div>
        </div>
      ),
      [STATUSES.APPROVED]: () => (
        <div className={`display_flex_row ${Styles.highlightedText}`}>
          <div>Connection Approved</div>
        </div>
      ),
      [STATUSES.REJECTED]: () => (
        <div className={`display_flex_row ${Styles.highlightedTextRejected}`}>
          <div>Rejected</div>
        </div>
      ),
    };

    const ActionComponent = statusActions[element.status];
    return ActionComponent ? <ActionComponent /> : null;
  },
);

StatusButton.displayName = "StatusButton";

// Chat Button Component
const ChatButton = React.memo(({ element, status, viewRightButtons }) => {
  const activeMain = useSelector((state) => state?.CapacitySlice);
  const tenderSpecefic = useSelector(
    (state) => state?.auctionSlice?.tenderSpecefic,
  );
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );
  const dispatch = useDispatch();
  const shouldShowChat = [
    STATUSES.FINAL,
    STATUSES.TERMS_INITIATED,
    STATUSES.SCHEDULED,
    STATUSES.PENDING,
    STATUSES.TERMS_ACCEPTED,
    STATUSES.APPROVED,
    STATUSES.AGREEMENT_ACCEPTED,
  ].includes(status);

  if (!shouldShowChat) return null;

  const handleOpenChat = (fromName, toName) => {
    let chatId;

    if (role === "CONSUMER") {
      chatId = `${fromName?.split("_")?.[0]}_${toName?.split("_")?.[0]}`;
    } else {
      chatId = `${toName?.split("_")?.[0]}_${fromName?.split("_")?.[0]}`;
    }
    openBilateralChat(dispatch, {
      header: element?.name,
      fromUser: activeMain?.activeMain?.name,
      toUser: element?.name,
      chatId: chatId,
      hidechat: !viewRightButtons,
      tenderName: tenderSpecefic?.tenderNumber || null,
    });
  };

  return (
    <>
      {/* <button
        type="button"
        onClick={() => {
          handleOpenChat(activeMain?.activeMain?.name, element?.name);
        }}
        className={Styles.chatBtn}
      >
        <span>Chat</span>
      </button> */}
    </>
  );
});

ChatButton.displayName = "ChatButton";

// Listing Card Component
const ListingCard = React.memo(
  ({
    element,
    setViewProfileDetails,
    activeMain,
    setOpenViewProfileModal,
    setSelectedListingData,
    handlePreviewGeneralTerms,
    setOpenInjectionModal,
    setActiveId,
    handleConnection,
    setActiveCounterId,
    setOpenEditListingModal,
    setClickedBtn,
    setConnectionDetails,
    finFinal,
    isReverseAuction,
    searchCapId,
    originalTenderCloseDateTime,
    stateLabel,
  }) => {
    const [showAllBids, setShowAllBids] = useState(false);

    // Helper for status check
    const shouldApplyStyle = [
      STATUSES.FINAL,
      STATUSES.TERMS_ACCEPTED,
      STATUSES.AGREEMENT_ACCEPTED,
      STATUSES.TERMS_INITIATED,
      STATUSES.APPROVED,
      STATUSES.PENDING,
      STATUSES.SCHEDULED,
    ].includes(element?.status);

    // Reverse-auction listings always surface the counterparty name (the API
    // provides generatorName/consumerName regardless of listing status), so
    // treat them like the "styled" statuses for name/label rendering.
    const showEntityName = shouldApplyStyle || isReverseAuction;

    const isStatusVisible = (status) =>
      [
        STATUSES.FINAL,
        STATUSES.TERMS_ACCEPTED,
        STATUSES.TERMS_INITIATED,
        STATUSES.FINAL,
        STATUSES.AGREEMENT_ACCEPTED,
        STATUSES.SCHEDULED,
        STATUSES.PENDING,
      ].includes(status);

    const shouldShowRightSideBtn = [
      STATUSES.FINAL,
      STATUSES.PENDING,
      STATUSES.TERMS_INITIATED,
      STATUSES.TERMS_ACCEPTED,
      STATUSES.FINAL,
      STATUSES.AGREEMENT_ACCEPTED,
      STATUSES.SCHEDULED,
    ].includes(element.status);

    const mainScreenMatchData = useSelector(
      (state) => state?.CapacitySlice?.mainScreenActiveConnection,
    );
    const role = useSelector(
      (state) => state?.persistedReducer?.user?.activeRole,
    )?.toLowerCase();

    let raViewData = mainScreenMatchData?.filter(
      (ele) =>
        element?.id ===
        (role === "consumer"
          ? ele?.capacityGenerator?.id
          : ele?.capacityConsumer?.id),
    )?.[0]?.bilateralConnections?.[0];

    let raViewDataList = mainScreenMatchData?.filter(
      (ele) =>
        element?.id ===
        (role === "consumer"
          ? ele?.capacityGenerator?.id
          : ele?.capacityConsumer?.id),
    )?.[0]?.bilateralConnections;

    const trader = useSelector((state) => state?.persistedReducer);

    const traderParent = trader?.roles?.includes("TRADER");
    const userId = trader?.user?.user?.userId;

    const activeData = useSelector((state) => state?.CapacitySlice?.activeMain);

    const traderRFQRA = useSelector(
      (state) => state?.CapacitySlice?.mainScreenActiveConnection,
    );

    const isOnDashboard = window.location.pathname.includes("/dashboard");
    const isTraderUnassigned = activeData?.traderAssigned === 0;
    const isAssignedGenTrader = userId === traderRFQRA?.[0]?.genTrader;

    const location = useLocation();
    const isTenderActivePage = location.pathname.includes("/tender_active");
    const isFromArchive =
      new URLSearchParams(location.search).get("source") === "archive";

    let viewRightButtons;
    if (isTenderActivePage) {
      viewRightButtons = isTenderActivePage;
    } else if (traderParent) {
      viewRightButtons =
        isAssignedGenTrader || userId === activeData?.traderAssigned;
    } else if (role === "consumer") {
      viewRightButtons = isTraderUnassigned;
    } else if (role === "generator" && !isOnDashboard) {
      viewRightButtons = isAssignedGenTrader && isTraderUnassigned;
    } else {
      viewRightButtons = isOnDashboard && isTraderUnassigned;
    }

    // Helper function to determine System T&C URL based on route and stateLabel
    const getSystemTnCUrl = () => {
      const isTenderActivePage =
        window.location.pathname.includes("/tender_active");

      if (isTenderActivePage) {
        if (stateLabel === "OD") {
          return "https://otc2-dev.s3.ap-south-1.amazonaws.com/assets/GNA+Energy+-+System+T%26C+-+RFP_RFQ.pdf";
        }
        return "https://otc2-dev.s3.ap-south-1.amazonaws.com/assets/GNA+Energy+-+System+T%26C+-+RFP_RFQ_other.pdf";
      }

      return "https://otc2-dev.s3.ap-south-1.amazonaws.com/assets/GNA+Energy+-+System+T%26C+-+Bilateral.pdf";
    };

    return (
      <div
        style={{
          position: "relative",
          marginTop: isReverseAuction && element?.status ? "16px" : "0px",
        }}
      >
        {isReverseAuction && element?.status && (
          <div
            style={{
              position: "absolute",
              top: "8px",
              right: "16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "4px",
              zIndex: 1,
            }}
          >
            <div
              style={{
                backgroundColor: STATUS_COLORS[element.status]?.bg ?? "#e8f4ea",
                color: STATUS_COLORS[element.status]?.text ?? "#2e7d32",
                padding: "2px 10px",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                textTransform: "capitalize",
              }}
            >
              {STATUS_LABELS[element.status] ??
                element.status?.split("_")?.join(" ")}
            </div>
          </div>
        )}
        <div
          className={`display_flex_column ${Styles.availableListingMainContainer} ${Styles.availableCardCounterparty}`}
        >
          {/* Header */}
          <div
            className={`display_flex_row width_95_padding_5 ${Styles.availableListingHeader}`}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div className={Styles.listHeaderName}>
                {showEntityName && (
                  <div
                    style={{
                      marginBottom: "4px",
                      color: "var(--secondary-color-text)",
                    }}
                    className={Styles.listHeaderName}
                  >
                    {element?.generatorName || element?.consumerName || "NA"}
                  </div>
                )}
                <span style={{ color: "var(--secondary-color-text)" }}>
                  {element?.name || "NA"}
                </span>
              </div>
              {isTenderActivePage && element?.contractUniqueId && (
                <div
                  className={Styles.contractIdLine}
                  title={element.contractUniqueId}
                >
                  Contract ID:
                  <span className={Styles.contractIdValue}>
                    {element.contractUniqueId}
                  </span>
                </div>
              )}
              {/* <div
              style={{ cursor: "pointer" }}
              type="button"
              onClick={() => {
                setViewProfileDetails(element);
                setOpenViewProfileModal(true);
              }}
              className={Styles.listHeaderC_R}
            >
              View Profile{" "}
              <span>
                <CallMadeIcon
                  style={{
                    width: "9px",
                    height: "9px",
                    marginBottom: "12px",
                  }}
                />
              </span>
            </div> */}
              {element?.creditRating?.toUpperCase() !== "UNKNOWN" && (
                <div className={Styles.listHeaderC_R}>
                  Credit Rating {element?.creditRating || "NA"}
                </div>
              )}
            </div>
            {!isReverseAuction && (
              <div
                className={`display_flex_row_gap_8 ${Styles.listHeaderExpDate}`}
              >
                <AccessTimeIcon style={{ fontSize: "14px" }} />
                <span className="warning">Expires on:</span>
                <span>
                  {formatDate(element?.listingExpDate)} -{" "}
                  {formatSlotTime(element?.listingExpTime)}
                </span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className={`display_flex_row ${Styles.availableListingDetails}`}>
            {isReverseAuction ? (
              <div style={{ width: "100%" }}>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "var(--primary-color-text)",
                    marginBottom: "6px",
                  }}
                >
                  Selected Bids
                </div>
                <div className="display_flex_row tableHeading_type2">
                  <div style={{ width: "30%" }}>Start & End Date</div>
                  <div style={{ width: "20%" }}>Start & End Time</div>
                  <div style={{ width: "20%" }}>Quantity (MW)</div>
                  <div style={{ width: "20%" }}>Energy Rate (₹/kWh)</div>
                </div>
                {(() => {
                  const allSlots =
                    raViewDataList || (raViewData ? [raViewData] : []);
                  const displaySlots = showAllBids
                    ? allSlots
                    : allSlots?.slice(0, 1);
                  return (
                    <>
                      {displaySlots?.map((slot, i) => (
                        <div key={i} className="display_flex_row tableDetails">
                          <div style={{ width: "30%" }}>
                            {formatDate(slot?.dateStartAt)} -{" "}
                            {formatDate(slot?.dateEndAt)}
                          </div>
                          <div style={{ width: "20%" }}>
                            {formatSlotTime(slot?.slotStartAt)} -{" "}
                            {formatSlotTime(slot?.slotEndAt)}
                          </div>
                          <div style={{ width: "20%" }}>
                            {slot?.quantity || "—"}
                          </div>
                          <div style={{ width: "20%" }}>
                            {slot?.amount || "—"}
                          </div>
                        </div>
                      ))}
                      {allSlots?.length > 1 && (
                        <div
                          onClick={() => setShowAllBids((prev) => !prev)}
                          style={{
                            position: "absolute",
                            bottom: "9px",
                            left: "43%",
                            cursor: "pointer",
                            backgroundColor:
                              "var(--primary-color-background-thin)",
                            color: "var(--primary-color-text)",
                            fontSize: "8px",
                            fontWeight: 400,
                            height: "16px",
                            width: "160px",
                            borderRadius: "5px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                            margin: "6px auto 0 auto",
                            boxShadow: "0px 1px 6px 0px #00000014",
                          }}
                        >
                          <span>
                            {showAllBids
                              ? "Hide Selected Bids"
                              : "View All Selected Bids"}
                          </span>
                          <span
                            style={{
                              display: "inline-block",
                              transform: showAllBids
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              fontSize: "10px",
                              lineHeight: 1,
                            }}
                          >
                            ▾
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <>
                <div className="display_flex_column_gap_4">
                  <div>Start & End Date</div>
                  <div style={{ fontWeight: 500 }}>
                    {formatDate(element?.slots[0]?.dateStartAt)} -{" "}
                    {formatDate(element?.slots[0]?.dateEndAt)}
                  </div>
                </div>
                <div className="display_flex_column_gap_4">
                  <div>Start & End Time</div>
                  <div style={{ fontWeight: 500 }}>
                    {formatSlotTime(element?.slots[0]?.slotStartAt)} -{" "}
                    {formatSlotTime(element?.slots[0]?.slotEndAt)}
                  </div>
                </div>
                <div className="display_flex_column_gap_4">
                  <div>
                    Quantity <span style={{ fontSize: "10px" }}>(MW)</span>{" "}
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {element?.slots[0]?.sellEnergy ||
                      element?.slots[0]?.purchaseEnergy}
                  </div>
                </div>
                <div className="display_flex_column_gap_4">
                  <div>
                    Energy Rate{" "}
                    <span style={{ fontSize: "10px" }}>(₹/kWh)</span>
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {viewRightButtons
                      ? element?.slots[0]?.energyRateType === "FIXED"
                        ? (element?.slots[0]?.sellingCost ??
                          element?.slots[0]?.landingCost)
                        : element?.slots[0]?.energyRateType === "DAM_PLUS"
                          ? `DAM+${
                              element?.slots[0]?.sellingCost ??
                              element?.slots[0]?.landingCost
                            }`
                          : `DAM-${
                              element?.slots[0]?.sellingCost ??
                              element?.slots[0]?.landingCost
                            }`
                      : "-"}
                  </div>
                </div>

                {element?.comment && (
                  <div
                    style={{ width: "100%" }}
                    className="display_flex_column_gap_4"
                  >
                    <div>Comment</div>
                    <div style={{ fontWeight: 500 }}>
                      <span
                        title={
                          element?.comment?.length > 60
                            ? element?.comment
                            : undefined
                        }
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          maxWidth: "850px",
                          cursor:
                            element?.comment?.length > 60
                              ? "pointer"
                              : "default",
                        }}
                      >
                        {element?.comment
                          ? element.comment.length > 60
                            ? element.comment.slice(0, 60) + "..."
                            : element.comment
                          : "NA"}
                      </span>
                    </div>
                  </div>
                )}

                <div
                  type="button"
                  onClick={() => {
                    setOpenInjectionModal(true);
                    setActiveId(element?.id);
                  }}
                  className={Styles.viewMoreElem}
                >
                  View All Requisitions
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className={`width_95_padding_5 display_flex_row ${Styles.availableListingFooter}`}
          >
            <div
              style={{
                gap: "0px",
                flexGrow: 1,
                marginRight: "15px",
                justifyContent: "flex-start",
                overflowX: "auto",
                whiteSpace: "nowrap",
                minHeight: "40px",
              }}
              className={`display_flex_row_gap_8 ${Styles.availableListingFooterMain}`}
            >
              {!(
                (role === "generator" || role === "consumer") &&
                isTenderActivePage
              ) && (
                <button
                  title={
                    element?.termsDocumentId
                      ? "View General Terms & Conditions"
                      : "Specific T&C not available"
                  }
                  onClick={() =>
                    handlePreviewGeneralTerms(element?.termsDocumentId)
                  }
                  style={{
                    cursor: element?.termsDocumentId
                      ? "pointer"
                      : "not-allowed",
                    opacity: element?.termsDocumentId ? 1 : 0.6,
                  }}
                  disabled={!element?.termsDocumentId}
                  className={`display_flex_row ${Styles.availableListingFooterText}`}
                >
                  <span>Specific T&C</span>
                  <CallMadeIcon
                    style={{ fontSize: "8px", marginTop: "-10px" }}
                  />
                </button>
              )}

              {/* <button
                onClick={() => {
                  handlePreviewGeneralTerms(getSystemTnCUrl(), {
                    hideHeader: true,
                  });
                }}
                style={{
                  cursor: "pointer",
                }}
                className={`display_flex_row ${Styles.availableListingFooterText}`}
              >
                <span>T&C</span>
                <CallMadeOutlinedIcon
                  style={{ fontSize: "10px", marginTop: "-10px" }}
                />
              </button> */}

              {(isStatusVisible(element?.status) || isReverseAuction) && (
                <LeftSideBtns
                  clickedIndex={element?.index}
                  isReverseAuction={isReverseAuction}
                  searchCapId={searchCapId}
                  reverseAuctionBids={() => {
                    setOpenInjectionModal(true);
                    setActiveId(element?.id);
                  }}
                  viewRightButtons={viewRightButtons}
                />
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {viewRightButtons && (
                <ChatButton
                  element={element}
                  status={element?.status}
                  viewRightButtons={viewRightButtons}
                />
              )}
              {viewRightButtons && (
                <StatusButton
                  element={element}
                  handleConnection={handleConnection}
                  setActiveCounterId={setActiveCounterId}
                  setOpenEditListingModal={setOpenEditListingModal}
                  setClickedBtn={setClickedBtn}
                  setConnectionDetails={setConnectionDetails}
                />
              )}

              {shouldShowRightSideBtn && (
                <RightSideBtn
                  finFinal={finFinal}
                  clickedIndex={element?.index}
                  isReverseAuction={isReverseAuction}
                  searchCapId={searchCapId}
                  originalTenderCloseDateTime={originalTenderCloseDateTime}
                  viewRightButtons={viewRightButtons}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ListingCard.displayName = "ListingCard";

// Main Component
const AvailableListing = ({
  energyRateSortDir,
  quantitySortDir,
  activeFilter,
  filterState,
  showBackbtn,
  setListingCount,
  isReverseAuction = false,
  searchCapId,
  originalTenderCloseDateTime,
  stateLabel,
}) => {
  // State
  const [openEditListingModal, setOpenEditListingModal] = useState(false);
  const [viewProfileDetails, setViewProfileDetails] = useState(null);
  const [proceedToBilateralConfirm, setProceedToBilateralConfirm] =
    useState(false);
  const [proceedToBilateralLoading, setProceedToBilateralLoading] =
    useState(false);
  const [openViewProfileModal, setOpenViewProfileModal] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [connectionDetails, setConnectionDetails] = useState(false);
  const [clickedBtn, setClickedBtn] = useState("");
  const [modalData, setModalData] = useState(null);
  const [activeCounterId, setActiveCounterId] = useState("");
  const [connectModal, setConnectModal] = useState(false);
  const [openInjectionModal, setOpenInjectionModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [finalizeTermsModal, setFinalizeTermsModal] = useState(false);
  const [editTermsModal, setEditTermsModal] = useState(false);
  const [acceptTermsModal, setAcceptTermsModal] = useState(false);
  const selfCapacityLoading = useSelector(
    (state) => state?.CapacitySlice?.fetchSelfCapacityLoading,
  );
  const [finFinal, setFinFinal] = useState({});
  const mainScreenDataLoading = useSelector(
    (state) => state?.CapacitySlice?.fetchMainScreenActiveConnectionLoading,
  );

  // Hooks
  const dispatch = useDispatch();
  const { handlePreviewGeneralTerms, PdfPreviewModal } = usePdfPreview();

  // Redux selectors
  const user = useSelector(
    (state) => state?.persistedReducer?.userType,
  )?.toLowerCase();
  const activeMain = useSelector((state) => state?.CapacitySlice);
  const params = new URLSearchParams(window.location.search);
  const activeMainIdFromUrl = Number(params.get("id"));

  const storeActiveMain = useSelector(
    (state) => state?.CapacitySlice?.activeMain?.id,
  );

  const activeMainId = activeMainIdFromUrl || storeActiveMain;

  const list = useSelector((state) => {
    return state?.CapacitySlice?.list;
  });
  const loading = useSelector(
    (state) => state?.CapacitySlice?.fetchCounterPartListLoading,
  );
  const rawCounterList = useSelector(
    (state) => state?.CapacitySlice?.counterList,
  );
  const counterList = useMemo(() => rawCounterList || [], [rawCounterList]);

  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection,
  );
  const viewModalDataMap = useMemo(() => {
    const map = {};

    // Add entries from mainScreenMatchData (connections)
    if (Array.isArray(mainScreenMatchData)) {
      mainScreenMatchData.forEach((item) => {
        const slots =
          user === USER_TYPES.CONSUMER
            ? item?.capacityGenerator?.slots || []
            : item?.capacityConsumer?.slots || [];

        // Use counterparty ID as key
        const counterpartyId =
          user === USER_TYPES.CONSUMER
            ? item?.capacityGenerator?.id
            : item?.capacityConsumer?.id;

        if (counterpartyId) {
          map[counterpartyId] = slots;
        }
      });
    }

    // Add entries from counterList (rawCounterList) - direct listings
    if (Array.isArray(counterList)) {
      counterList.forEach((item) => {
        if (item?.id && Array.isArray(item?.slots)) {
          map[item.id] = item.slots;
        }
      });
    }

    return map;
  }, [mainScreenMatchData, counterList, user]);

  const raViewDataList = useMemo(
    () =>
      mainScreenMatchData?.filter(
        (ele) =>
          activeId ===
          (user === USER_TYPES.CONSUMER
            ? ele?.capacityGenerator?.id
            : ele?.capacityConsumer?.id),
      )?.[0]?.bilateralConnections,
    [mainScreenMatchData, activeId, user],
  );

  const id = useSelector((state) => state?.CapacitySlice?.activeListingId);
  const ledgerBids = useSelector((state) => {
    return state?.CapacitySlice?.ledgerBids;
  });

  const selfCapacityById = useSelector(
    (state) => state?.CapacitySlice?.getSelfListingById,
  );

  useEffect(() => {
    if (
      selfCapacityById &&
      typeof selfCapacityById === "object" &&
      Object.keys(selfCapacityById).length > 0
    ) {
      setFinFinal(selfCapacityById);
    }
  }, [selfCapacityById]);

  useEffect(() => {
    if (
      window.location.pathname.includes("/ledger") ||
      window.location.pathname.includes("/tender_active")
    ) {
      dispatch(fetchSelfCapacityById({ user, id: activeMainIdFromUrl }));
      const payload = {
        id: activeMainIdFromUrl,
        energySourceType: "",
        energyFuelType: "",
        tag: "",
      };
      dispatch(fetchCounterPartList({ user, payload, curr: 0 }));
    } else {
      const foundItem = list?.find((ele) => ele?.id === activeMainId);
      if (
        foundItem &&
        typeof foundItem === "object" &&
        Object.keys(foundItem).length > 0
      ) {
        setFinFinal(foundItem);
      }
    }
  }, [dispatch, user, activeMainIdFromUrl, list, activeMainId]);

  // Transform data function

  const transformConnectionData = useCallback(
    (connections, isMainScreen = false) => {
      if (!Array.isArray(connections)) return [];

      return connections.map((item, idx) => {
        const slots = [];
        const bilateralConnections = item.bilateralConnections || [];

        // Get date/time from the opposite capacity object
        const capacityData =
          user === USER_TYPES.CONSUMER
            ? item?.capacityGenerator
            : item?.capacityConsumer;

        for (let i = 0; i < bilateralConnections.length; i++) {
          const connection = bilateralConnections[i];
          if (connection.status === STATUSES.REJECTED) continue;

          // Get slot data from the opposite user type
          const slotData =
            user === USER_TYPES.CONSUMER
              ? connection.generatorSlot
              : connection.consumerSlot;

          slots.push({
            ...slotData,
            // Override with capacity-level dates/times if available, otherwise use connection-level, then slot-level
            slotStartAt:
              capacityData?.slots?.[i]?.slotStartAt ||
              connection.slotStartAt ||
              slotData.slotStartAt,
            slotEndAt:
              capacityData?.slots?.[i]?.slotEndAt ||
              connection.slotEndAt ||
              slotData.slotEndAt,
            dateStartAt:
              capacityData?.slots?.[i]?.dateStartAt ||
              connection.dateStartAt ||
              slotData.dateStartAt,
            dateEndAt:
              capacityData?.slots?.[i]?.dateEndAt ||
              connection.dateEndAt ||
              slotData.dateEndAt,
            energyRateType:
              capacityData?.slots?.[i]?.energyRateType ||
              connection.energyRateType ||
              slotData.energyRateType,
            amount: capacityData?.slots?.[i]?.amount || connection.amount,
            quantity: capacityData?.slots?.[i]?.quantity || connection.quantity,
          });
        }

        const baseData =
          user === USER_TYPES.CONSUMER
            ? item?.capacityGenerator
            : item?.capacityConsumer;

        return {
          ...baseData,
          slots,
          index: idx,
          contractUniqueId: item.contractUniqueId,
          ...(isMainScreen && {
            status: item.status,
            initiatorType: item.initiatorType,
          }),
        };
      });
    },
    [user],
  );

  const mainScreenConnections = useMemo(
    () => transformConnectionData(mainScreenMatchData, true),
    [mainScreenMatchData, transformConnectionData],
  );

  const termsIntiateConnections = useMemo(() => {
    // Transform both lists
    const mainScreenConnections = transformConnectionData(
      mainScreenMatchData,
      true,
    );

    const counterConnections = counterList;

    // Create a map for mainScreenConnections by id
    const mainScreenMap = new Map(
      mainScreenConnections?.map((item) => [item.id, item]),
    );

    // Merge: if id exists in both, take from mainScreenConnections
    const merged = [
      ...mainScreenConnections,
      ...counterConnections?.filter((item) => !mainScreenMap.has(item.id)),
    ];

    // Sort: status "PENDING" on top
    merged.sort((a, b) => {
      if (a.status === "PENDING" && b.status !== "PENDING") return -1;
      if (b.status === "PENDING" && a.status !== "PENDING") return 1;
      return 0;
    });

    return merged;
  }, [mainScreenMatchData, counterList, transformConnectionData]);

  function allGenTermsAcceptedAndConTermsNot(mainScreenConnections) {
    return (
      Array.isArray(mainScreenConnections) &&
      mainScreenConnections.some(
        (obj) =>
          obj.conTermsAcceptanceStatus === false &&
          obj.genTermsAcceptanceStatus === true,
      )
    );
  }

  // Final list based on status
  const finalList = useMemo(() => {
    const { status } = finFinal || {};

    if (
      status === STATUSES.FINAL ||
      status === STATUSES.TERMS_ACCEPTED ||
      status === STATUSES.CLOSED ||
      status === STATUSES.SCHEDULED ||
      status === STATUSES.AGREEMENT_ACCEPTED
    ) {
      return mainScreenConnections;
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.GENERATOR &&
      mainScreenMatchData.some((obj) => obj.initiatorType === "generator") &&
      !mainScreenMatchData.some((obj) =>
        obj.bilateralConnections?.some(
          (connection) => connection.status === "ACCEPTED",
        ),
      )
    ) {
      return [
        ...mainScreenConnections,
        ...counterList.filter((item) => item.status === STATUSES.APPROVED),
      ];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.GENERATOR &&
      mainScreenMatchData.some(
        (obj) => obj["capacityGenerator"].status === "PARTIAL_FINAL",
      )
    ) {
      return [...mainScreenConnections];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.GENERATOR &&
      mainScreenMatchData.some((obj) => obj.initiatorType === "consumer")
    ) {
      return [...mainScreenConnections, ...counterList];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.GENERATOR &&
      mainScreenMatchData?.some((obj) => obj.initiatorType === "consumer")
    ) {
      return [...mainScreenConnections];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.CONSUMER &&
      mainScreenMatchData?.some((obj) => obj.initiatorType === "consumer") &&
      !mainScreenMatchData.some((obj) =>
        obj.bilateralConnections?.some(
          (connection) => connection.status === "ACCEPTED",
        ),
      )
    ) {
      return [
        ...mainScreenConnections,
        ...counterList.filter((item) => item.status === STATUSES.APPROVED),
      ];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.CONSUMER &&
      mainScreenMatchData?.some(
        (obj) =>
          obj.initiatorType === "generator" &&
          mainScreenMatchData?.some(
            (obj) =>
              obj[
                user === "consumer" ? "capacityConsumer" : "capacityGenerator"
              ].status === "PARTIAL_FINAL",
          ),
      )
    ) {
      return [...mainScreenConnections];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.CONSUMER &&
      mainScreenMatchData?.some(
        (obj) =>
          obj.initiatorType === "consumer" &&
          mainScreenMatchData?.some(
            (obj) =>
              obj[
                user === "consumer" ? "capacityConsumer" : "capacityGenerator"
              ].status === "PARTIAL_FINAL",
          ),
      )
    ) {
      return [...mainScreenConnections];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.GENERATOR &&
      mainScreenMatchData?.some(
        (obj) =>
          obj.initiatorType === "consumer" &&
          mainScreenMatchData?.some(
            (obj) =>
              obj[
                user === "consumer" ? "capacityConsumer" : "capacityGenerator"
              ].status === "PARTIAL_FINAL",
          ),
      )
    ) {
      return [...mainScreenConnections];
    }
    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.GENERATOR &&
      mainScreenMatchData?.some(
        (obj) =>
          obj.initiatorType === "generator" &&
          mainScreenMatchData?.some(
            (obj) =>
              obj[
                user === "consumer" ? "capacityConsumer" : "capacityGenerator"
              ].status === "PARTIAL_FINAL",
          ),
      )
    ) {
      return [...mainScreenConnections];
    }

    if (
      status === STATUSES.TERMS_INITIATED &&
      user === USER_TYPES.CONSUMER &&
      !allGenTermsAcceptedAndConTermsNot(mainScreenConnections)
    ) {
      return termsIntiateConnections;
    }
    return counterList;
  }, [
    finFinal,
    user,
    mainScreenMatchData,
    mainScreenConnections,
    counterList,
    termsIntiateConnections,
  ]);

  const filteredFinalList = useMemo(() => {
    if (!finalList) return [];
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    function isSameDay(date1, date2) {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    }

    function parseDate(dateStr) {
      return dateStr ? new Date(dateStr) : null;
    }

    switch (activeFilter) {
      case "FINALIZED":
        return finalList.filter((item) => item.status === STATUSES.FINAL);
      case "ACCEPTED":
        return finalList.filter((item) => item.status === STATUSES.APPROVED);
      case "RECEIVED/PLACED":
        return finalList.filter(
          (item) =>
            item.status === STATUSES.RECEIVED ||
            item.status === STATUSES.PLACED,
        );
      case "ACTIVE":
        return finalList.filter((item) => item.status === STATUSES.ACTIVE);
      case "INTRADAY":
        return finalList.filter((item) => {
          const start = parseDate(item?.slots?.[0]?.dateStartAt);
          const end = parseDate(item?.slots?.[0]?.dateEndAt);
          return start && end && isSameDay(start, end);
        });
      case "NEXT_DAY":
        return finalList.filter((item) => {
          const start = parseDate(item?.slots?.[0]?.dateStartAt);
          const end = parseDate(item?.slots?.[0]?.dateEndAt);
          return (
            start &&
            end &&
            isSameDay(start, tomorrow) &&
            isSameDay(end, tomorrow)
          );
        });
      case "UPTO_7_DAYS":
        return finalList.filter((item) => {
          const start = parseDate(item?.slots?.[0]?.dateStartAt);
          const end = parseDate(item?.slots?.[0]?.dateEndAt);
          if (!start || !end) return false;
          const diffDays = Math.abs((end - start) / (1000 * 60 * 60 * 24));
          return diffDays <= 7;
        });
      case "GREATER_THAN_7_DAYS":
        return finalList.filter((item) => {
          const start = parseDate(item?.slots?.[0]?.dateStartAt);
          const end = parseDate(item?.slots?.[0]?.dateEndAt);
          if (!start || !end) return false;
          const diffDays = Math.abs((end - start) / (1000 * 60 * 60 * 24));
          return diffDays > 7;
        });
      case "ALL":
      default:
        return finalList;
    }
  }, [finalList, activeFilter]);

  const filterByFilterState = useCallback((list, filterState) => {
    return list.filter((item) => {
      // Quantity
      const quantity =
        item?.slots?.[0]?.quantity ??
        item?.slots?.[0]?.sellEnergy ??
        item?.slots?.[0]?.purchaseEnergy ??
        0;
      if (
        quantity < filterState.quantity[0] ||
        quantity > filterState.quantity[1]
      ) {
        return false;
      }

      // Energy Rate
      const energyRate =
        item?.slots?.[0]?.amount ??
        item?.slots?.[0]?.sellingCost ??
        item?.slots?.[0]?.landingCost ??
        0;
      if (
        energyRate < filterState.energyRate[0] ||
        energyRate > filterState.energyRate[1]
      ) {
        return false;
      }

      // Credit Rating
      if (
        filterState.creditRating &&
        item?.creditRating !== filterState.creditRating
      ) {
        return false;
      }

      // Date Filter Logic - only apply if startDate is provided
      if (filterState.startDate) {
        const filterStart = new Date(filterState.startDate);
        const slotStart = item?.slots?.[0]?.dateStartAt
          ? new Date(item.slots[0].dateStartAt)
          : null;
        const slotEnd = item?.slots?.[0]?.dateEndAt
          ? new Date(item.slots[0].dateEndAt)
          : null;

        if (!slotStart || !slotEnd) {
          return false;
        }

        // If only startDate is provided
        if (!filterState.endDate) {
          // Show only items where slot overlaps with or starts on/after the filter date
          // Filter out items that end before the filter start date
          if (slotEnd < filterStart) {
            return false;
          }
        } else {
          // If both dates provided, check for overlap
          const filterEnd = new Date(filterState.endDate);
          // Filter out if there is NO overlap
          if (slotEnd < filterStart || slotStart > filterEnd) {
            return false;
          }
        }
      }

      // Time Filter Logic - only apply if startTime is provided
      if (filterState.startTime) {
        const slotStartTime = item?.slots?.[0]?.slotStartAt;
        const slotEndTime = item?.slots?.[0]?.slotEndAt;

        if (!slotStartTime || !slotEndTime) {
          return false;
        }

        // If only startTime is provided
        if (!filterState.endTime) {
          // Show only items where slot overlaps with or starts on/after the filter time
          if (slotEndTime < filterState.startTime) {
            return false;
          }
        } else {
          // If both times provided, check for overlap
          if (
            slotEndTime < filterState.startTime ||
            slotStartTime > filterState.endTime
          ) {
            return false;
          }
        }
      }

      return true;
    });
  }, []);

  const sortedCounterList = useMemo(() => {
    if (!filteredFinalList) return [];

    // Filter by filterState
    let filtered = filterByFilterState(filteredFinalList, filterState);

    // Sort as before
    if (energyRateSortDir) {
      filtered.sort((a, b) => {
        const aRate = getEnergyRate(a);
        const bRate = getEnergyRate(b);
        return energyRateSortDir === "asc" ? aRate - bRate : bRate - aRate;
      });
    } else if (quantitySortDir) {
      filtered.sort((a, b) => {
        const aQty = getQuantity(a);
        const bQty = getQuantity(b);
        return quantitySortDir === "asc" ? aQty - bQty : bQty - aQty;
      });
    }

    return filtered;
  }, [
    filteredFinalList,
    energyRateSortDir,
    quantitySortDir,
    filterState,
    filterByFilterState,
  ]);

  useEffect(() => {
    setListingCount(sortedCounterList.length);
  }, [sortedCounterList.length, setListingCount]);

  useEffect(() => {
    const statusesToFetch = [
      STATUSES.FINAL,
      STATUSES.TERMS_ACCEPTED,
      STATUSES.SCHEDULED,
      STATUSES.AGREEMENT_ACCEPTED,
      STATUSES.TERMS_INITIATE_AND_ACCEPT,
      STATUSES.TERMS_INITIATED,
      STATUSES.CLOSED,
    ];

    if (statusesToFetch.includes(finFinal?.status)) {
      dispatch(fetchMainScreenActiveConnection({ capId: finFinal.id }));
    }
  }, [finFinal?.id, finFinal?.status, dispatch]);

  useEffect(() => {
    if (id && connectionDetails && ledgerBids?.length === 0) {
      dispatch(fetchLedgerBids(id));
    }
  }, [dispatch, id, connectionDetails, ledgerBids?.length]);

  useEffect(() => {
    if (ledgerBids?.length > 0) {
      setModalData(ledgerBids);
    }
  }, [ledgerBids, connectionDetails]);

  // Event handlers
  const handleConnection = useCallback((connectionId) => {
    setActiveId(connectionId);
    setConnectModal(true);
  }, []);

  const handleCloseConnection = useCallback(() => {
    setConnectModal(false);
    setActiveId("");
  }, []);

  const handleConnect = useCallback(
    async (data) => {
      try {
        setConnectLoading(true);
        const response = await placeBidApi(data);

        if (response?.data?.code === 200) {
          const payload = {
            id,
            energySourceType: "",
            energyFuelType: "",
            tag: "",
          };
          dispatch(fetchCounterPartList({ user, payload, curr: 0 }));
          dispatch(fetchLedgerBids(id));
          setConnectModal(false);
        }
      } catch (error) {
      } finally {
        setConnectLoading(false);
      }
    },
    [dispatch, id, user],
  );

  const handleConnectionClose = useCallback(() => {
    setConnectionDetails(false);
  }, []);

  // Render helpers
  const userDataKey = user === USER_TYPES.CONSUMER ? "generator" : "consumer";

  const handleEditSubmit = useCallback(
    async (params, oldData) => {
      setEditLoading(true);
      const {
        listingExpDate,
        listingExpTime,
        termsExpDate,
        termsExpTime,
        bidEndAt,
        ...rest
      } = params;

      rest.name = finFinal.name;
      rest.id = finFinal.id;

      if (rest.termsDocumentId === null) {
        delete rest.termsDocumentId;
      }

      if (Array.isArray(rest.slots) && Array.isArray(oldData.slots)) {
        rest.slots = rest.slots.map((slot, idx) => {
          if (oldData.slots[idx] && oldData.slots[idx].id !== undefined) {
            return { ...slot, id: oldData.slots[idx].id };
          }
          return slot;
        });
      }

      function slotsChanged(newSlots, oldSlots) {
        if (
          !Array.isArray(newSlots) ||
          !Array.isArray(oldSlots) ||
          newSlots.length !== oldSlots.length
        ) {
          return true;
        }
        for (let i = 0; i < newSlots.length; i++) {
          const newSlot = newSlots[i];
          const oldSlot = oldSlots[i];
          const allKeys = new Set([
            ...Object.keys(newSlot),
            ...Object.keys(oldSlot),
          ]);
          for (const key of allKeys) {
            if (newSlot[key] !== oldSlot[key]) {
              return true;
            }
          }
        }
        return false;
      }

      const changedFields = {};
      for (const key in rest) {
        if (key === "slots") {
          if (slotsChanged(rest.slots, oldData.slots)) {
            changedFields.slots = rest.slots;
          }
        } else if (rest[key] !== oldData[key]) {
          changedFields[key] = rest[key];
        }
      }

      changedFields.id = finFinal.id;
      let res = await editListing(rest, user);
      if (res?.data?.code === 200) {
        setOpenEditListingModal(false);
        window.location.reload();
      }
      setEditLoading(false);
    },
    [finFinal, user],
  );

  const renderModals = useCallback(() => {
    return (
      <>
        {connectModal && (
          <PlaceBid
            useSecondaryAccent
            open={connectModal}
            handleClose={handleCloseConnection}
            handleSubmit={handleConnect}
            connectionText="Connection Request"
            selfData={activeMain?.activeMain}
            counterData={sortedCounterList?.find((ele) => ele?.id === activeId)}
            loading={connectLoading}
          />
        )}

        {openViewProfileModal && (
          <ViewProfile
            open={openViewProfileModal}
            handleClose={() => setOpenViewProfileModal(false)}
            data={viewProfileDetails}
          />
        )}

        {openInjectionModal && (
          <ViewInjectionModal
            useSecondaryAccent
            open={openInjectionModal}
            handleClose={() => {
              setOpenInjectionModal(false);
              setActiveId("");
            }}
            data={
              !window.location.pathname.includes("/tender_active")
                ? viewModalDataMap[activeId]
                : raViewDataList
            }
          />
        )}

        {connectionDetails && (
          <ConnectionModal
            open={
              modalData?.filter((i) => i[userDataKey]?.id === clickedBtn)[0]
            }
            wholeData={modalData?.filter(
              (i) => i[userDataKey]?.id === clickedBtn,
            )}
            activeId={activeMain?.activeListingId}
            handleClose={handleConnectionClose}
            connectionText="Connection Request"
            useSecondaryAccent
          />
        )}

        {openEditListingModal && (
          <ConGenListingModal
            editConnectionRequest={true}
            useSecondaryAccent
            editId={activeMain?.activeMain?.id}
            data={activeMain?.activeMain}
            open={openEditListingModal}
            loading={editLoading}
            activeCounrterId={activeCounterId}
            handleClose={() => setOpenEditListingModal(false)}
            handleSubmit={handleEditSubmit}
          />
        )}
      </>
    );
  }, [
    connectModal,
    handleCloseConnection,
    handleConnect,
    activeMain?.activeMain,
    activeMain?.activeListingId,
    sortedCounterList,
    connectLoading,
    openViewProfileModal,
    viewProfileDetails,
    openInjectionModal,
    connectionDetails,
    modalData,
    handleConnectionClose,
    openEditListingModal,
    editLoading,
    activeCounterId,
    handleEditSubmit,
    activeId,
    userDataKey,
    clickedBtn,
    raViewDataList,
    viewModalDataMap,
  ]);

  const groupedByStatus = useMemo(() => {
    // First, create the initial groups without any transformation
    const initialGroups = {};
    sortedCounterList.forEach((item) => {
      const key = item.status || "UNKNOWN";
      if (!initialGroups[key]) initialGroups[key] = [];
      initialGroups[key].push(item);
    });

    // Merge TERMS_INITIATED and PENDING into a single group
    const proceedBilateralItems = [
      ...(initialGroups[STATUSES.PENDING] || []),
      ...(initialGroups[STATUSES.TERMS_INITIATED] || []),
    ];

    // Only show "PROCEED_BILATERAL" if BOTH are present
    if (
      initialGroups[STATUSES.PENDING]?.length > 0 &&
      initialGroups[STATUSES.TERMS_INITIATED]?.length > 0
    ) {
      initialGroups["PROCEED_BILATERAL"] = proceedBilateralItems;
      delete initialGroups[STATUSES.PENDING];
      delete initialGroups[STATUSES.TERMS_INITIATED];
    }

    const pendingList = initialGroups[STATUSES.PENDING] || [];
    const editPending = pendingList.filter(
      (item) => item.initiatorType === user,
    );
    const otherPending = pendingList.filter(
      (item) => item.initiatorType !== user,
    );

    if (editPending.length) {
      initialGroups[EDIT_PENDING_STATUS] = editPending;
    }
    if (otherPending.length) {
      initialGroups[STATUSES.PENDING] = otherPending;
    } else {
      delete initialGroups[STATUSES.PENDING];
    }

    // Now check if we need to treat PENDING as APPROVED using the actual groups
    const shouldTreatPendingAsApproved = hasBothApprovedAndFinalizeGroups({
      groupedByStatus: initialGroups,
      mainScreenMatchData,
      user,
      STATUSES,
      sortedCounterList,
      hasApprovedStatus,
    });

    // If we need to transform, create new groups with PENDING items moved to APPROVED
    if (shouldTreatPendingAsApproved) {
      const transformedGroups = { ...initialGroups };

      const pendingForApprove = [
        ...(transformedGroups[STATUSES.PENDING] || []),
        ...(transformedGroups[EDIT_PENDING_STATUS] || []),
      ];

      if (pendingForApprove.length) {
        if (!transformedGroups[STATUSES.APPROVED]) {
          transformedGroups[STATUSES.APPROVED] = [];
        }
        transformedGroups[STATUSES.APPROVED].push(...pendingForApprove);
        delete transformedGroups[STATUSES.PENDING];
        delete transformedGroups[EDIT_PENDING_STATUS];
      }

      return transformedGroups;
    }

    return initialGroups;
  }, [sortedCounterList, mainScreenMatchData, user]);

  useEffect(() => {
    if (Object.keys(groupedByStatus).length > 0) {
      dispatch(checkApprovedAndPending(groupedByStatus));
    }
  }, [dispatch, groupedByStatus]);

  const marginTop = useMemo(() => {
    if (
      (finFinal?.status === "ACTIVE" ||
        finFinal?.status === "RECEIVED" ||
        finFinal?.status === "PLACED" ||
        finFinal?.status === "TERMS_INITIATED" ||
        finFinal?.status === "APPROVED") &&
      !showBackbtn
    ) {
      return "45px";
    } else if (
      (finFinal?.status === "ACTIVE" ||
        finFinal?.status === "RECEIVED" ||
        finFinal?.status === "PLACED" ||
        finFinal?.status === "TERMS_INITIATED" ||
        finFinal?.status === "APPROVED") &&
      showBackbtn
    ) {
      return "110px";
    } else if (!isReverseAuction && showBackbtn) {
      return "65px";
    }

    return "5px";
  }, [finFinal?.status, isReverseAuction, showBackbtn]);

  function hasBothApprovedAndFinalizeGroups({
    groupedByStatus,
    mainScreenMatchData,
    user,
    STATUSES,
    sortedCounterList,
    hasApprovedStatus,
  }) {
    const hasIsApproved =
      groupedByStatus[STATUSES.PENDING] &&
      mainScreenMatchData.every(
        (connection) =>
          ((user === "consumer" && connection?.genTermsAcceptanceStatus) ||
            (user === "generator" && connection?.conTermsAcceptanceStatus)) &&
          connection?.status === STATUSES.PENDING,
      );

    // Check for finalize group
    const hasFinalize =
      groupedByStatus[STATUSES.APPROVED] &&
      hasApprovedStatus(sortedCounterList);

    return hasIsApproved && hasFinalize;
  }

  const handleProceed = React.useCallback(async () => {
    // Function to get IDs where initiatorType matches user and status is PENDING
    const getFilteredIds = (data, currentUser) => {
      if (!Array.isArray(data)) return [];

      return data
        .filter(
          (item) =>
            item.initiatorType === currentUser && item.status === "PENDING",
        )
        .map((item) => item.id);
    };

    // Get the filtered IDs
    const filteredIds = getFilteredIds(mainScreenMatchData, user);

    if (filteredIds?.length === 0) {
      return ErrorMessage("Nothing to proceed");
    }
    let payload = {
      contractIds: filteredIds,
      capacityId: finFinal?.id,
    };

    try {
      setProceedToBilateralLoading(true);

      const res = await withdrawTerms(payload);
      if (res?.data?.code === 200) {
        dispatch(
          fetchActiveConnection({
            curr: 0,
            capId: finFinal?.id,
            isTermsAccepted: null,
          }),
        );
        dispatch(fetchMainScreenActiveConnection({ capId: finFinal?.id }));
        dispatch(fetchSelfCapacity({ user }));
        dispatch(fetchSelfCapacityById({ user, id: finFinal?.id }));
        setProceedToBilateralConfirm(false);
      }
    } catch (error) {
    } finally {
      setProceedToBilateralLoading(false);
    }
  }, [dispatch, finFinal?.id, mainScreenMatchData, user]);

  const trader = useSelector((state) => state?.persistedReducer);

  const traderParent = trader?.roles?.includes("TRADER");
  const userId = trader?.user?.user?.userId;

  const activeData = useSelector((state) => state?.CapacitySlice?.activeMain);
  const traderRFQRA = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection,
  );

  const viewRightButtons = traderParent
    ? userId === traderRFQRA?.[0]?.genTrader ||
      userId === activeData?.traderAssigned
    : activeData?.traderAssigned === 0;
  return (
    <div style={{ position: "relative", marginTop: marginTop }}>
      {renderModals()}
      <PdfPreviewModal />

      {finalizeTermsModal && (
        <TermsCondition
          open={finalizeTermsModal}
          handleClose={() => setFinalizeTermsModal(false)}
          listingId={finFinal?.id}
          useSecondaryAccent
        />
      )}
      {acceptTermsModal && (
        <TermsCondition
          open={acceptTermsModal}
          handleClose={() => setAcceptTermsModal(false)}
          listingId={finFinal?.id}
          type={"accept"}
          useSecondaryAccent
        />
      )}
      {/* {editTermsModal && (
        <TermsCondition
          open={editTermsModal}
          handleClose={() => setEditTermsModal(false)}
          listingId={finFinal?.id}
          isEditTerms={true}
        />
      )} */}

      <ConfirmationModal
        open={proceedToBilateralConfirm}
        handleClose={() => setProceedToBilateralConfirm(false)}
        handleSubmit={() => handleProceed()}
        header={"Confirm Proceed to Bilateral"}
        data={
          "Proceeding to bilateral negotiations will automatically reject all pending negotiations currently in progress. This action is irreversible. Do you wish to continue?"
        }
        loading={proceedToBilateralLoading}
      />

      {loading || selfCapacityLoading || mainScreenDataLoading ? (
        <AvailableListingSkeleton count={4} />
      ) : !sortedCounterList?.length ? (
        <div style={{ minHeight: "calc(100vh - 200px)", position: "relative" }}>
          <EmptyState />
        </div>
      ) : (
        Object.entries(groupedByStatus)
          .sort(([statusA], [statusB]) => {
            const getPriority = (status) => {
              const isFinalize =
                status === STATUSES.APPROVED &&
                hasApprovedStatus(sortedCounterList);
              if (isFinalize) return 1;

              const isApproved =
                status === STATUSES.PENDING &&
                mainScreenMatchData.every(
                  (connection) =>
                    ((user === USER_TYPES.CONSUMER &&
                      connection?.genTermsAcceptanceStatus) ||
                      (user === USER_TYPES.GENERATOR &&
                        connection?.conTermsAcceptanceStatus)) &&
                    connection?.status === STATUSES.PENDING,
                );
              if (isApproved) return 2;

              if (status === STATUSES.APPROVED) return 3;
              if (
                status === STATUSES.RECEIVED ||
                status === STATUSES.PLACED ||
                status === EDIT_PENDING_STATUS || // treat edit-pending same priority bucket as other mid states
                status === STATUSES.PENDING
              )
                return 4;

              if (status === STATUSES.ACTIVE) return 7;

              return 5;
            };

            const priorityA = getPriority(statusA);
            const priorityB = getPriority(statusB);

            return priorityA - priorityB;
          })
          .map(([status, items]) => {
            const isApproved =
              !hasBothApprovedAndFinalizeGroups({
                groupedByStatus,
                mainScreenMatchData,
                user,
                STATUSES,
                sortedCounterList,
                hasApprovedStatus,
              }) &&
              status === STATUSES.PENDING &&
              mainScreenMatchData.some(
                (connection) =>
                  ((user === USER_TYPES.CONSUMER &&
                    connection?.genTermsAcceptanceStatus) ||
                    (user === USER_TYPES.GENERATOR &&
                      connection?.conTermsAcceptanceStatus)) &&
                  connection?.status === STATUSES.PENDING,
              );

            const proceedBilateral = status === "PROCEED_BILATERAL";

            const finalize = hasBothApprovedAndFinalizeGroups({
              groupedByStatus,
              mainScreenMatchData,
              user,
              STATUSES,
              sortedCounterList,
              hasApprovedStatus,
            })
              ? ((status === STATUSES.PENDING &&
                  mainScreenMatchData.every(
                    (connection) =>
                      ((user === USER_TYPES.CONSUMER &&
                        connection?.genTermsAcceptanceStatus) ||
                        (user === USER_TYPES.GENERATOR &&
                          connection?.conTermsAcceptanceStatus)) &&
                      connection?.status === STATUSES.PENDING,
                  )) ||
                  status === STATUSES.APPROVED) &&
                hasApprovedStatus(sortedCounterList)
              : status === STATUSES.APPROVED &&
                hasApprovedStatus(sortedCounterList);

            const editTerms =
              (status === STATUSES.PENDING || status === EDIT_PENDING_STATUS) &&
              !isApproved &&
              !finalize;

            const groupStyle = isApproved
              ? {
                  border: "2px solid var(--secondary-color-text)",
                  borderRadius: "10px",
                  marginBottom: "20px",
                  paddingBottom: "10px",
                  background: "#f9f9f9",
                }
              : finalize
                ? {
                    border: "2px solid var(--secondary-color-text)",
                    borderRadius: "10px",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    background: "#f9f9f9",
                  }
                : proceedBilateral
                  ? {
                      border: "2px solid var(--secondary-color-text)",
                      borderRadius: "10px",
                      marginBottom: "20px",
                      paddingBottom: "10px",
                      background: "#f9f9f9",
                    }
                  : // : editTerms
                    // ? {
                    //     border: "2px solid var(--primary-color-text)",
                    //     borderRadius: "10px",
                    //     marginBottom: "20px",
                    //     paddingBottom: "10px",
                    //     background: "#f9f9f9",
                    //   }
                    {};

            return (
              <div key={status} style={groupStyle}>
                {items.map((element) => {
                  return (
                    <ListingCard
                      finFinal={finFinal}
                      key={element?.id}
                      setOpenViewProfileModal={setOpenViewProfileModal}
                      element={element}
                      setViewProfileDetails={setViewProfileDetails}
                      activeMain={activeMain}
                      handlePreviewGeneralTerms={handlePreviewGeneralTerms}
                      setOpenInjectionModal={setOpenInjectionModal}
                      setActiveId={setActiveId}
                      handleConnection={handleConnection}
                      setActiveCounterId={setActiveCounterId}
                      setOpenEditListingModal={setOpenEditListingModal}
                      setClickedBtn={setClickedBtn}
                      setConnectionDetails={setConnectionDetails}
                      isReverseAuction={isReverseAuction}
                      searchCapId={searchCapId}
                      originalTenderCloseDateTime={originalTenderCloseDateTime}
                      stateLabel={stateLabel}
                    />
                  );
                })}

                {viewRightButtons && isApproved && (
                  <div className={Styles.listingCardFooter}>
                    <button
                      onClick={() => {
                        setAcceptTermsModal(true);
                      }}
                      type="button"
                    >
                      Approve Finalized T&C
                    </button>
                  </div>
                )}
                {viewRightButtons && proceedBilateral && (
                  <div className={Styles.listingCardFooter}>
                    <button
                      onClick={() => {
                        setProceedToBilateralConfirm(true);
                      }}
                      type="button"
                    >
                      Proceed to Bilateral
                    </button>
                  </div>
                )}
                {/* {viewRightButtons && finalize && (
                  <div className={Styles.listingCardFooter}>
                    <button
                      onClick={() => {
                        setFinalizeTermsModal(true);
                      }}
                      type="button"
                    >
                      Finalize T&C
                    </button>
                  </div>
                )} */}
                {/* {editTerms && (
                  <div className={Styles.listingCardFooter}>
                    <button
                      onClick={() => {
                        setEditTermsModal(true);
                      }}
                      type="button"
                    >
                      Edit T&C
                    </button>
                  </div>
                )} */}
              </div>
            );
          })
      )}
    </div>
  );
};

export default AvailableListing;
